/**
 * Read-only mirror of the clinic's clinical-document schema, so documents a
 * clinic shares with the client (stored as notes: type in `category`, fields
 * JSON-encoded in `body`) render as proper documents here instead of raw JSON.
 */
export const DOC_TYPES = {
  consultation: {
    dateField: 'date',
    fields: [
      { k: 'date', t: 'date' },
      { k: 'chiefComplaint', t: 'multiline' },
      { k: 'expectations', t: 'multiline' },
      { k: 'medicalHistory', t: 'multiline' },
      { k: 'previousProcedures', t: 'multiline' },
      { k: 'currentMedications', t: 'multiline' },
      { k: 'allergies', t: 'multiline' },
      { k: 'contraindications', t: 'multiline' },
      { k: 'skinType', t: 'select' },
      { k: 'facialAssessment', t: 'multiline' },
      { k: 'physicianObservations', t: 'multiline' },
      { k: 'treatmentRecommendations', t: 'multiline' },
    ],
  },
  treatment_record: {
    dateField: 'treatmentDate',
    fields: [
      { k: 'treatmentDate', t: 'date' },
      { k: 'physician', t: 'text' },
      { k: 'procedureType', t: 'text' },
      { k: 'injectionAreas', t: 'chips' },
      { k: 'productsUsed', t: 'chips' },
      { k: 'batchNumbers', t: 'chips' },
      { k: 'quantity', t: 'text' },
      { k: 'parameters', t: 'multiline' },
      { k: 'patientResponse', t: 'multiline' },
      { k: 'followUpRecommendations', t: 'multiline' },
    ],
  },
  treatment_plan: {
    dateField: 'date',
    fields: [
      { k: 'date', t: 'date' },
      { k: 'priorityConcerns', t: 'multiline' },
      { k: 'recommendedProcedures', t: 'chips' },
      { k: 'treatmentSequence', t: 'multiline' },
      { k: 'estimatedTimeline', t: 'text' },
      { k: 'maintenanceSchedule', t: 'multiline' },
      { k: 'homeSkincare', t: 'multiline' },
    ],
  },
  follow_up: {
    dateField: 'visitDate',
    fields: [
      { k: 'visitDate', t: 'date' },
      { k: 'satisfaction', t: 'select' },
      { k: 'healingProgress', t: 'multiline' },
      { k: 'clinicalOutcomes', t: 'multiline' },
      { k: 'complications', t: 'multiline' },
      { k: 'touchUpRequirements', t: 'multiline' },
      { k: 'nextAppointment', t: 'date' },
    ],
  },
  follow_up_schedule: {
    dateField: 'treatmentDate',
    fields: [
      { k: 'treatmentDate', t: 'date' },
      { k: 'clinicFollowUpDate', t: 'date' },
      { k: 'captureDates', t: 'captureSchedule' },
      { k: 'smsReminder', t: 'switch' },
    ],
  },
};

const STRUCTURED = (cat) => cat && DOC_TYPES[cat] && cat !== 'note';

/** Turn a shared note into either a structured document or a plain note. */
export function parseSharedDoc(note) {
  if (STRUCTURED(note.category)) {
    let values = {};
    try { const p = JSON.parse(note.body || '{}'); values = (p && p.values) || {}; } catch { values = {}; }
    return {
      id: note.id, kind: 'doc', type: note.category, values,
      clinicName: note.clinicName, createdAt: note.createdAt,
    };
  }
  // The clinic "note" type stores { values: { date, note } } as JSON.
  if (note.category === 'note') {
    try {
      const p = JSON.parse(note.body || '{}');
      if (p && p.values) {
        return {
          id: note.id, kind: 'note', body: p.values.note || '', date: p.values.date,
          category: null, clinicName: note.clinicName, createdAt: note.createdAt,
        };
      }
    } catch { /* fall through to legacy plain note */ }
  }
  return {
    id: note.id, kind: 'note', body: note.body || '', date: null,
    category: note.category || null, clinicName: note.clinicName, createdAt: note.createdAt,
  };
}

export function docDate(d) {
  if (d.kind === 'doc') {
    const df = DOC_TYPES[d.type].dateField;
    return (df && d.values[df]) || d.createdAt;
  }
  return d.date || d.createdAt;
}
